// src/components/CreateTasksFromAssessmentsModal.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react/dist/iconify.js";
import { getAssignedAssessments } from "../redux/slices/formSlice";
import { addTaskAdmin } from "../redux/slices/attendanceSlice";

const CreateTasksFromAssessmentsModal = ({ onClose, onSave, isSaving }) => {
  const dispatch = useDispatch();
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start_time: "",
    end_time: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { assignedAssessments, isAssignedAssessmentsLoading } = useSelector(
    (state) => state.form,
  );

  // Fetch assigned assessments on mount
  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    setLoading(true);
    await dispatch(getAssignedAssessments(1));
    setLoading(false);
  };

  // Filter eligible assessments:
  // 1. submitted_assessment_id is null (not already a form/submitted)
  // 2. start_time and end_time are null (attendance not done)
  const eligibleAssessments = () => {
    let assessments = [];

    if (assignedAssessments?.data?.data) {
      assessments = assignedAssessments.data.data;
    } else if (Array.isArray(assignedAssessments?.data)) {
      assessments = assignedAssessments.data;
    } else if (Array.isArray(assignedAssessments)) {
      assessments = assignedAssessments;
    }

    return assessments.filter((assessment) => {
      const hasNoForm = !assessment.submitted_assessment_id;
      const hasNoAttendance = !assessment.start_time && !assessment.end_time;
      return hasNoForm && hasNoAttendance;
    });
  };

  const handleCardClick = (assessment) => {
    setSelectedAssessment(assessment);
    // Pre-fill title and description based on assessment data
    setFormData({
      title: `${assessment.category_name} - ${assessment.facility_name}`,
      description: `Assessment for ${assessment.category_name} at ${assessment.facility_name}\nAssigned to: ${assessment.user_name}\nCustomer Group: ${assessment.customer_group_name || "N/A"}`,
      start_time: "",
      end_time: "",
    });
    setError(null);
  };

  const handleBackToList = () => {
    setSelectedAssessment(null);
    setFormData({
      title: "",
      description: "",
      start_time: "",
      end_time: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }
    if (!formData.start_time) {
      setError("Start time is required");
      return;
    }
    if (!formData.end_time) {
      setError("End time is required");
      return;
    }

    // Validate that end time is after start time
    if (new Date(formData.end_time) <= new Date(formData.start_time)) {
      setError("End time must be after start time");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    // Format dates for API (YYYY-MM-DD HH:MM:SS)
    const formatDateTimeForAPI = (dateTimeStr) => {
      const date = new Date(dateTimeStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const taskData = {
      title: formData.title,
      description: formData.description,
      start_time: formatDateTimeForAPI(formData.start_time),
      end_time: formatDateTimeForAPI(formData.end_time),
      assigned_assessment_id: selectedAssessment.id,
      facility_id: selectedAssessment.facility_id,
      user_id: selectedAssessment.user_id,
    };

    try {
      const result = await dispatch(addTaskAdmin(taskData)).unwrap();

      if (result.success) {
        if (onSave) {
          onSave({
            success: true,
            message: "Task created successfully!",
            task: result.data,
            assessment: selectedAssessment,
          });
        }
        onClose();
      } else {
        setError(result.message || "Failed to create task");
      }
    } catch (err) {
      setError(err.message || "An error occurred while creating the task");
    } finally {
      setIsSubmitting(false);
    }
  };

  const eligible = eligibleAssessments();
  const isLoading = isAssignedAssessmentsLoading || loading;

  return (
    <div>
      {!selectedAssessment ? (
        // List View - Show eligible assessments
        <>
          <div className="mb-3">
            <p className="text-muted small">
              Click on any assessment below to create a task with attendance.
              <br />
              <strong>{eligible.length}</strong> eligible assessments found.
            </p>
          </div>

          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading assessments...</span>
              </div>
              <p className="mt-2 text-muted small">Fetching assessments...</p>
            </div>
          ) : eligible.length === 0 ? (
            <div className="text-center py-4">
              <Icon
                icon="mdi:checkbox-blank-circle-outline"
                width="48"
                height="48"
                className="text-muted mb-2"
              />
              <p className="text-muted">
                No eligible assessments found.
                <br />
                <small className="d-block mt-1">
                  Assessments must have:
                  <br />• No submitted form (submitted_assessment_id = null)
                  <br />• No attendance recorded (start_time and end_time =
                  null)
                </small>
              </p>
            </div>
          ) : (
            <div
              className="assessment-list"
              style={{ maxHeight: "500px", overflowY: "auto" }}>
              {eligible.map((assessment) => (
                <div
                  key={assessment.id}
                  className="mb-4 p-10 border rounded hover-bg-light"
                  style={{
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: "#fff",
                    fontSize: "13px",
                  }}
                  onClick={() => handleCardClick(assessment)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                    e.currentTarget.style.borderColor = "#8B2885";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff";
                    e.currentTarget.style.borderColor = "#dee2e6";
                  }}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div
                        className="fw-semibold mb-1"
                        style={{ color: "#8B2885", fontSize: "14px" }}>
                        {assessment.category_name}
                      </div>
                      <div className="mb-1">
                        <span className="fw-semibold">Facility:</span>{" "}
                        {assessment.facility_name}
                      </div>
                      <div
                        className="text-muted mb-1"
                        style={{ fontSize: "12px" }}>
                        <Icon
                          icon="mdi:account"
                          width="12"
                          height="12"
                          className="me-1"
                        />
                        {assessment.user_name || "Unknown User"}
                        <span className="mx-2">•</span>
                        <Icon
                          icon="mdi:email"
                          width="12"
                          height="12"
                          className="me-1"
                        />
                        {assessment.user_email || "No email"}
                      </div>
                      {assessment.customer_group_name && (
                        <div
                          className="text-muted"
                          style={{ fontSize: "12px" }}>
                          <Icon
                            icon="mdi:tag"
                            width="12"
                            height="12"
                            className="me-1"
                          />
                          {assessment.customer_group_name}
                        </div>
                      )}
                    </div>
                    <Icon
                      icon="mdi:chevron-right"
                      width="20"
                      height="20"
                      color="#8B2885"
                    />
                  </div>
                  <div className="mt-1">
                    <span
                      className="badge bg-secondary"
                      style={{ fontSize: "10px" }}>
                      ID: {assessment.id}
                    </span>
                    <span
                      className="badge bg-info ms-1"
                      style={{ fontSize: "10px" }}>
                      Cat: {assessment.category_id}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        // Form View - Create task for selected assessment
        <div style={{ maxHeight: "calc(70vh - 100px)", overflowY: "auto" }}>
          <button
            onClick={handleBackToList}
            className="btn btn-link text-decoration-none mb-2 p-0"
            style={{ color: "#8B2885", fontSize: "14px" }}>
            <Icon
              icon="mdi:arrow-left"
              width="18"
              height="18"
              className="me-1"
            />
            Back to assessments list
          </button>

          <div
            className="mb-3 p-2 bg-light rounded"
            style={{ fontSize: "13px" }}>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <div>
                  <strong>Facility:</strong> {selectedAssessment.facility_name}
                </div>
                <div>
                  <strong>User:</strong> {selectedAssessment.user_name}
                </div>
                <div>
                  <strong>Category:</strong> {selectedAssessment.category_name}
                </div>
              </div>
              <div className="text-end">
                <span
                  className="badge bg-secondary"
                  style={{ fontSize: "10px" }}>
                  ID: {selectedAssessment.id}
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-2">
              <label
                className="form-label fw-semibold"
                style={{ fontSize: "13px", marginBottom: "4px" }}>
                Task Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="title"
                className="form-control form-control-sm"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter task title"
                required
                style={{ fontSize: "14px" }}
              />
            </div>

            <div className="mb-2">
              <label
                className="form-label fw-semibold"
                style={{ fontSize: "13px", marginBottom: "4px" }}>
                Description
              </label>
              <textarea
                name="description"
                className="form-control form-control-sm"
                rows="2"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter task description"
                style={{
                  fontSize: "14px",
                  resize: "vertical",
                  minHeight: "80px",
                }}
              />
            </div>

            <div className="row g-2 mb-2">
              <div className="col-6">
                <label
                  className="form-label fw-semibold"
                  style={{ fontSize: "13px", marginBottom: "4px" }}>
                  Start Time <span className="text-danger">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="start_time"
                  className="form-control form-control-sm"
                  value={formData.start_time}
                  onChange={handleInputChange}
                  required
                  style={{ fontSize: "13px" }}
                />
              </div>
              <div className="col-6">
                <label
                  className="form-label fw-semibold"
                  style={{ fontSize: "13px", marginBottom: "4px" }}>
                  End Time <span className="text-danger">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="end_time"
                  className="form-control form-control-sm"
                  value={formData.end_time}
                  onChange={handleInputChange}
                  required
                  style={{ fontSize: "13px" }}
                />
              </div>
            </div>

            {error && (
              <div
                className="alert alert-danger py-1 mb-2"
                style={{ fontSize: "12px" }}>
                <Icon
                  icon="mdi:alert-circle"
                  width="14"
                  height="14"
                  className="me-1"
                />
                {error}
              </div>
            )}

            <div className="d-flex gap-2 mt-3">
              <button
                type="button"
                className="btn btn-secondary btn-sm flex-grow-1"
                onClick={handleBackToList}
                disabled={isSubmitting}
                style={{ fontSize: "14px", padding: "6px 12px" }}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-sm flex-grow-1"
                style={{
                  backgroundColor: "#8B2885",
                  color: "white",
                  fontSize: "14px",
                  padding: "6px 12px",
                }}
                disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Creating...
                  </>
                ) : (
                  <>
                    <Icon
                      icon="mdi:check"
                      width="16"
                      height="16"
                      className="me-1"
                    />
                    Create Task
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CreateTasksFromAssessmentsModal;
